"use client";

import { useMemo, useState } from "react";
import {
  Field,
  TextInput,
  SelectInput,
  CheckboxRow,
  SegmentedControl,
  ResultBanner,
  PrintButton,
  PrintFooter,
  trackToolResult,
  type Verdict,
} from "@/components/tools/ui";

/* Drafts a simple fire risk assessment around the HSE / gov.uk five-step process
   under the Regulatory Reform (Fire Safety) Order 2005: identify fire hazards,
   identify people at risk, evaluate/remove/reduce and protect, record, and review.

   Compliance (YMYL): the FRA is a statutory duty of the Responsible Person and is
   NOT delegable. This tool drafts a record for SIMPLER, lower-risk premises only.
   Premises with sleeping accommodation (hotels, HMOs, care homes), and large or
   complex buildings, need a competent fire risk assessor (e.g. a PAS 79 assessment)
   — the tool flags that and never claims to be a completed or certified FRA. */

type State = "" | "ok" | "action" | "na";

const SLEEPING_TYPES = new Set(["care", "hotel-hmo", "residential"]);

const IGNITION = [
  "Electrical equipment, wiring or overloaded sockets",
  "Heaters, boilers or heating plant",
  "Cooking / kitchen equipment",
  "Hot works, naked flames or smoking areas",
  "Machinery / friction / hot surfaces",
  "Arson / deliberate ignition (external access, waste)",
];
const FUEL = [
  "Paper, card, packaging and general waste",
  "Flammable liquids, gases, aerosols or solvents",
  "Furniture, soft furnishings and textiles",
  "Stored stock or combustible materials",
  "Building materials / flammable cladding or linings",
];
const PEOPLE = [
  "Employees and contractors",
  "Members of the public / visitors",
  "Lone or out-of-hours workers",
  "Disabled, elderly, children or others needing assistance to escape",
  "People unfamiliar with the building layout",
  "People sleeping on the premises",
];

interface PItem { id: string; label: string; critical?: boolean }
interface PGroup { id: string; title: string; items: PItem[] }
const PRECAUTIONS: PGroup[] = [
  {
    id: "escape", title: "Means of escape",
    items: [
      { id: "exits", label: "Enough exits for the number of people, leading to a place of safety", critical: true },
      { id: "routes-clear", label: "Escape routes and final exits kept clear and unobstructed", critical: true },
      { id: "travel", label: "Travel distances to an exit are reasonable; no dead-ends relied on", critical: true },
      { id: "doors-openable", label: "Final exit doors openable without a key from the inside" },
    ],
  },
  {
    id: "detect", title: "Detection, warning & lighting",
    items: [
      { id: "alarm", label: "Fire detection and warning appropriate to the premises, tested and serviced", critical: true },
      { id: "emerg-light", label: "Emergency lighting on escape routes where needed, tested" },
      { id: "signage", label: "Fire-action notices and escape-route / exit signage in place" },
    ],
  },
  {
    id: "protect", title: "Fire-fighting & protection",
    items: [
      { id: "extinguishers", label: "Suitable extinguishers, sited and serviced (BS 5306)" },
      { id: "doors", label: "Fire doors and compartmentation intact and self-closing", critical: true },
      { id: "services", label: "Electrical, gas and heating installations maintained" },
    ],
  },
  {
    id: "manage", title: "Management & people",
    items: [
      { id: "plan", label: "Written emergency plan; assembly point; means of calling the fire service", critical: true },
      { id: "training", label: "Staff fire-safety instruction and evacuation drills" },
      { id: "peep", label: "PEEPs in place for anyone who needs help to evacuate" },
      { id: "record", label: "Significant findings recorded (legally required at 5+ employees)" },
    ],
  },
];
const ALL_P = PRECAUTIONS.flatMap((g) => g.items);

const OPTIONS = [
  { value: "ok" as const, label: "In place", activeCls: "border-emerald bg-emerald-tint text-emerald-deep" },
  { value: "action" as const, label: "Action", activeCls: "border-risk-veryhigh bg-risk-veryhigh/10 text-risk-veryhigh" },
  { value: "na" as const, label: "N/A", activeCls: "border-line-2 bg-mist text-muted" },
];

export function FireRiskAssessment() {
  const [premises, setPremises] = useState("");
  const [rp, setRp] = useState("");
  const [type, setType] = useState("");
  const [employees, setEmployees] = useState("");
  const [hazards, setHazards] = useState<Record<string, boolean>>({});
  const [people, setPeople] = useState<Record<string, boolean>>({});
  const [prec, setPrec] = useState<Record<string, State>>({});

  const toggle = (set: typeof setHazards) => (id: string, v: boolean) => set((s) => ({ ...s, [id]: v }));
  const sleeping = SLEEPING_TYPES.has(type);

  const result = useMemo(() => {
    const answered = ALL_P.filter((i) => prec[i.id]);
    const actions = ALL_P.filter((i) => prec[i.id] === "action");
    const critical = actions.filter((i) => i.critical);
    const remaining = ALL_P.length - answered.length;
    let level: Verdict = "low";
    let title = "";
    if (answered.length === 0 && !sleeping) {
      title = "Work through the fire precautions below";
    } else if (sleeping) {
      level = "veryhigh";
      title = "Sleeping risk — a competent fire risk assessor is needed";
    } else if (critical.length > 0) {
      level = "veryhigh";
      title = `${critical.length} critical action${critical.length > 1 ? "s" : ""} — urgent`;
    } else if (actions.length > 0) {
      level = "medium";
      title = `${actions.length} action${actions.length > 1 ? "s" : ""} to complete`;
    } else if (remaining === 0) {
      level = "low";
      title = "No significant findings outstanding on this checklist";
    } else {
      level = "low";
      title = `${answered.length}/${ALL_P.length} reviewed — keep going`;
    }
    return { actions, critical, remaining, answered: answered.length, level, title };
  }, [prec, sleeping]);

  if (result.remaining === 0 && result.answered > 0) {
    trackToolResult("fire-risk-assessment", { actions: result.actions.length, critical: result.critical.length, sleeping });
  }

  return (
    <div className="space-y-8">
      {/* Premises */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Premises / address"><TextInput value={premises} onChange={(e) => setPremises(e.target.value)} placeholder="e.g. Unit 4, Maple Trading Estate" /></Field>
        <Field label="Responsible Person"><TextInput value={rp} onChange={(e) => setRp(e.target.value)} placeholder="Employer / occupier" /></Field>
        <Field label="Premises type">
          <SelectInput value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">Select…</option>
            <option value="office">Office</option>
            <option value="retail">Shop / retail</option>
            <option value="warehouse">Warehouse / industrial</option>
            <option value="restaurant">Restaurant / commercial kitchen</option>
            <option value="education">School / education (no sleeping)</option>
            <option value="care">Care home (sleeping)</option>
            <option value="hotel-hmo">Hotel / HMO (sleeping)</option>
            <option value="residential">Residential block — communal areas (sleeping)</option>
            <option value="other">Other</option>
          </SelectInput>
        </Field>
        <Field label="Number of employees" hint="A written record is legally required at 5 or more."><TextInput value={employees} onChange={(e) => setEmployees(e.target.value)} inputMode="numeric" placeholder="e.g. 8" /></Field>
      </div>

      {sleeping && (
        <ResultBanner level="veryhigh" title="This premises has sleeping accommodation — get a competent fire risk assessor">
          <p>Premises where people sleep (hotels, HMOs, care homes, residential blocks) are higher-risk and complex. The Responsible Person should commission a <strong>competent fire risk assessor</strong> (e.g. a PAS&nbsp;79 assessment). Use this tool only to prepare — it does not produce a suitable and sufficient FRA for sleeping risk.</p>
        </ResultBanner>
      )}

      {/* Live result */}
      {result.answered > 0 && !sleeping && (
        <ResultBanner level={result.level} title={result.title}>
          {result.critical.length > 0 ? (
            <p>Critical fire-safety measures are missing or inadequate — these put life safety at risk and need acting on without delay. Record them and act now.</p>
          ) : result.actions.length > 0 ? (
            <p>Some measures need action. Record them as significant findings with a responsible person and target date, then re-check.</p>
          ) : (
            <p>Nothing outstanding on this checklist. Record the assessment, keep it under review, and re-do it after any fire or significant change.</p>
          )}
        </ResultBanner>
      )}

      {/* Step 1 — hazards */}
      <Section step="1" title="Identify the fire hazards">
        <CheckGroup title="Sources of ignition" items={IGNITION} state={hazards} onToggle={toggle(setHazards)} idp="ig" />
        <CheckGroup title="Sources of fuel" items={FUEL} state={hazards} onToggle={toggle(setHazards)} idp="fu" />
      </Section>

      {/* Step 2 — people */}
      <Section step="2" title="Identify the people at risk">
        <CheckGroup title="Who could be harmed" items={PEOPLE} state={people} onToggle={toggle(setPeople)} idp="pe" />
      </Section>

      {/* Step 3 — precautions */}
      <Section step="3" title="Evaluate the existing fire precautions">
        {PRECAUTIONS.map((g) => (
          <fieldset key={g.id} className="rounded-2xl border border-line bg-white p-5 shadow-soft">
            <legend className="px-2 font-display text-base font-bold text-ink">{g.title}</legend>
            <div className="mt-3 space-y-4">
              {g.items.map((item) => (
                <div key={item.id} className="grid gap-2 border-b border-line pb-4 last:border-0 last:pb-0 sm:grid-cols-[1fr_auto] sm:items-start sm:gap-4">
                  <p className="text-sm text-body">
                    {item.label}
                    {item.critical && <span className="ml-2 rounded bg-risk-veryhigh/10 px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wider text-risk-veryhigh">Critical</span>}
                  </p>
                  <SegmentedControl legend={item.label} name={item.id} value={prec[item.id] ?? ""} onChange={(v) => setPrec((s) => ({ ...s, [item.id]: v as State }))} options={OPTIONS} className="sm:w-[250px]" />
                </div>
              ))}
            </div>
          </fieldset>
        ))}
      </Section>

      {/* Action list (prints) */}
      {result.actions.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <h3 className="font-display text-base font-bold text-amber-900">Significant findings — actions {premises && `· ${premises}`}</h3>
          <ul className="mt-3 space-y-2 text-sm text-amber-900">
            {result.actions.map((a) => (
              <li key={a.id} className="flex gap-2"><span className="mt-0.5 shrink-0 font-bold">{a.critical ? "‼" : "•"}</span><span>{a.label}{a.critical && " (critical)"}</span></li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <PrintButton tool="fire-risk-assessment" label="Print / save this fire risk assessment" />
        <span className="text-xs text-muted">{result.answered}/{ALL_P.length} precautions reviewed</span>
      </div>

      <PrintFooter toolName="Fire Risk Assessment" />
    </div>
  );
}

function Section({ step, title, children }: { step: string; title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink text-xs font-bold text-white">{step}</span>
        <h2 className="font-display text-xl font-bold text-ink">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function CheckGroup({ title, items, state, onToggle, idp }: { title: string; items: string[]; state: Record<string, boolean>; onToggle: (id: string, v: boolean) => void; idp: string }) {
  return (
    <div className="rounded-2xl border border-line bg-white p-5 shadow-soft">
      <p className="font-display text-sm font-bold text-ink">{title}</p>
      <div className="mt-3 space-y-2.5">
        {items.map((label, i) => {
          const id = `${idp}-${i}`;
          return <CheckboxRow key={id} checked={!!state[id]} onChange={(v) => onToggle(id, v)}>{label}</CheckboxRow>;
        })}
      </div>
    </div>
  );
}
